import { useState, useCallback, useEffect } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import EmployeeService from "../../../../services/employee_service";
import RoleService from "../../../../services/role_service";
import { GLOBAL_STATUS } from "../../../../constants/common";

/**
 * useEmployees
 * Hook de lógica para o módulo de Colaboradores.
 * Usa EmployeeService diretamente (não usePagination) para garantir
 * o mapeamento correto de role/idRole.
 */
export const useEmployees = () => {
    const { idTenant, user } = useTenant();
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Busca Colaboradores via EmployeeService (já faz tradução role/idRole)
    const fetchEmployees = useCallback(async () => {
        if (!idTenant) return;
        try {
            setLoading(true);
            const list = await EmployeeService.getEmployees(idTenant);
            setEmployees(list || []);
        } catch (error) {
            console.error("Erro ao buscar colaboradores:", error);
            toast.error("Erro ao carregar colaboradores.");
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    // Busca os Cargos (Roles) disponíveis para o formulário
    const fetchRoles = useCallback(async () => {
        if (!idTenant) return;
        try {
            const list = await RoleService.getRoles(idTenant);
            setRoles(list || []);
        } catch (error) {
            console.error("Erro ao buscar cargos disponíveis:", error);
        }
    }, [idTenant]);

    // Executa na montagem
    useEffect(() => {
        fetchEmployees();
        fetchRoles();
    }, [fetchEmployees, fetchRoles]);

    const refresh = useCallback(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    /**
     * Salvar um Colaborador
     */
    const saveEmployee = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            const savedEmployee = await EmployeeService.save(formData, idTenant, user, employees, roles);
            toast.success(formData.id ? "Colaborador atualizado!" : "Novo colaborador criado com sucesso!");
            refresh();
            return savedEmployee;
        } catch (err) {
            console.error("Erro ao salvar colaborador:", err);
            toast.error(err?.message || "Erro ao salvar colaborador.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, employees, roles, refresh]);

    /**
     * Ativar / Inativar um Colaborador
     */
    const changeEmployeeStatus = useCallback(async (employeeId, currentStatus) => {
        if (!idTenant) return;

        const newStatus = currentStatus === GLOBAL_STATUS.ACTIVE ? GLOBAL_STATUS.INACTIVE : GLOBAL_STATUS.ACTIVE;
        const actionLabel = newStatus === GLOBAL_STATUS.ACTIVE ? 'Ativar' : 'Inativar';

        const confirmed = await ConfirmDialog({
            title: `${actionLabel} Colaborador?`,
            text: `Tem certeza que deseja ${actionLabel.toLowerCase()} o acesso deste colaborador?`,
            confirmButtonText: "Sim, confirmar",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            const employeeData = employees.find(e => e.id === employeeId);
            if (employeeData) {
                await EmployeeService.save({ ...employeeData, status: newStatus }, idTenant, user, employees, roles);
                toast.success(`Colaborador ${newStatus === GLOBAL_STATUS.ACTIVE ? 'Ativado' : 'Inativado'}!`);
                refresh();
            }
            return true;
        } catch (err) {
            console.error("Erro ao inativar colaborador:", err);
            toast.error("Não foi possível mudar o status.");
            return false;
        }
    }, [idTenant, user, employees, roles, refresh]);

    return {
        employees,
        roles,
        loading,
        isSaving,
        actions: {
            saveEmployee,
            changeEmployeeStatus,
            refresh
        }
    };
};
